import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AuthService } from '../auth.service';
import { Subscription } from 'rxjs';

import { AuthConstants } from '../auth.constants';
import { PageTitleService } from '../../navigation/title/title.service';

@Component({
  selector: 'multisite-password-reset-form',
  templateUrl: './password-reset-form.component.html',
  styleUrls: ['./password-reset-form.component.scss'],

  standalone: false,
})
export class PasswordResetFormComponent implements OnInit, OnDestroy {
  @Input() email: string;
  @Input() resetToken: string;
  showWarningAlert: boolean = false;
  showSuccessAlert: boolean = false;
  PasswordResetForm: FormGroup;
  loading: boolean;
  errorMessage: string;
  hideBackendErrorMessage: boolean;
  passwordResetSubscription: Subscription;
  authConstants: any;

  constructor(private authService: AuthService, private titleService : PageTitleService) {
    this.authConstants = AuthConstants;
  }

  hideAlerts() {
    this.showSuccessAlert = false;
    this.showWarningAlert = false;
  }

  // The error keys, the field styling, the messages and the submit button all derive from these
  // getters, which read the current values, so they cannot disagree. Do not add Angular validators.
  private formatErrorKey(value: string | null): string | null {
    if (!value) {
      return 'common.required';
    }
    if (value.length > this.authConstants.passwordMaxLength) {
      return 'common.too_long';
    }
    if (!this.authConstants.passwordRegexPattern.test(value)) {
      return 'authentication.password_min_length';
    }
    return null;
  }

  get passwordErrorKey(): string | null {
    return this.formatErrorKey(this.PasswordResetForm.controls.password.value);
  }

  get repeatPasswordErrorKey(): string | null {
    const password = this.PasswordResetForm.controls.password.value;
    const repeatPassword = this.PasswordResetForm.controls.repeatPassword.value;
    const formatErrorKey = this.formatErrorKey(repeatPassword);
    if (formatErrorKey) {
      return formatErrorKey;
    }
    return password && password !== repeatPassword
      ? 'authentication.password_confirmation_error'
      : null;
  }

  // An untouched empty field is neutral, not invalid; 'dirty' means the user has typed in it.
  get showPasswordError(): boolean {
    return this.PasswordResetForm.controls.password.dirty && !!this.passwordErrorKey;
  }

  get showRepeatPasswordError(): boolean {
    return this.PasswordResetForm.controls.repeatPassword.dirty && !!this.repeatPasswordErrorKey;
  }

  get isPasswordValid(): boolean {
    return this.PasswordResetForm.controls.password.dirty && !this.passwordErrorKey;
  }

  get isRepeatPasswordValid(): boolean {
    return this.PasswordResetForm.controls.repeatPassword.dirty && !this.repeatPasswordErrorKey;
  }

  get canSubmit(): boolean {
    return !this.passwordErrorKey && !this.repeatPasswordErrorKey;
  }

  sendRequest() {
    if (!this.canSubmit) {
      return;
    }
    this.loading = true;
    this.hideAlerts();
    this.errorMessage = null;

    this.passwordResetSubscription = this.authService
      .resetPassword(
        this.email,
        this.PasswordResetForm.controls.password.value,
        this.PasswordResetForm.controls.repeatPassword.value,
        this.resetToken
      )
      .subscribe(
        (response) => {
          this.loading = false;
          this.PasswordResetForm.reset(); // this change also calls hideAlerts()
          this.showSuccessAlert = true;
        },
        (errorMessage) => {
          this.errorMessage = errorMessage;
          this.showWarningAlert = true;
          this.loading = false;
        }
      );
  }
  generateForm (){
    this.PasswordResetForm = new FormGroup({
      password: new FormControl<string | null>(null),
      repeatPassword: new FormControl<string | null>(null),
    });
  }
  ngOnInit(): void {
    this.loading = false;
    this.generateForm ();
    this.PasswordResetForm.valueChanges.subscribe(() => this.hideAlerts());
    this.titleService.setTitle(null);
  }
  ngOnDestroy() {
    if (this.passwordResetSubscription) {
      this.passwordResetSubscription.unsubscribe();
    }
  }
}
